// frontend/src/components/LiveAlertsWidget.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, Fuel, Wrench, Calendar, ExternalLink, X } from 'lucide-react';
import { notificationManager } from '../manager_notifications/notificationManager';

// ─── Web Audio bell — no external file needed ─────────────────────────────────
const playBellSound = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();

    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, ctx.currentTime);
    osc1.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 1.2);
    gain1.gain.setValueAtTime(0.5, ctx.currentTime);
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.2);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);

    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1760, ctx.currentTime);
    osc2.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.8);
    gain2.gain.setValueAtTime(0.25, ctx.currentTime);
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);

    osc1.start(ctx.currentTime);
    osc1.stop(ctx.currentTime + 1.2);
    osc2.start(ctx.currentTime);
    osc2.stop(ctx.currentTime + 0.8);
  } catch (e) {
    // Browser without Web Audio API — fail silently
  }
};

// ─── Only these three types trigger the widget ────────────────────────────────
const WATCHED_TYPES = new Set(['fuel', 'maintenance', 'appointment_request']);

// ─── Navigate using the existing App.js custom event ─────────────────────────
const navigateTo = (tab) => {
  window.dispatchEvent(new CustomEvent('navigate', { detail: { tab } }));
};

// ─── Icon per alert type ──────────────────────────────────────────────────────
const AlertTypeIcon = ({ type }) => {
  if (type === 'fuel_request')        return <Fuel  size={15} className="text-orange-400 flex-shrink-0" />;
  if (type === 'maintenance_request') return <Wrench size={15} className="text-red-400    flex-shrink-0" />;
  if (type === 'appointment_request') return <Calendar size={15} className="text-blue-400  flex-shrink-0" />;
  return <Bell size={15} className="text-gray-400 flex-shrink-0" />;
};

// ─── Label per alert type ─────────────────────────────────────────────────────
const typeLabel = (type) => {
  if (type === 'fuel_request')        return 'Fuel Request';
  if (type === 'maintenance_request') return 'Maintenance Request';
  if (type === 'appointment_request') return 'Appointment Request';
  return 'Request';
};

// ─── Destination tab per alert type ──────────────────────────────────────────
const destTab = (type) => {
  if (type === 'appointment_request') return 'schedule';
  return 'clientRequests';
};

// ─── Destination label per alert type ────────────────────────────────────────
const destLabel = (type) => {
  if (type === 'appointment_request') return 'Schedule';
  return 'Client Requests';
};

// ─── Single alert card ────────────────────────────────────────────────────────
const AlertCard = ({ alert, blinking, onDismiss }) => {
  const tab = destTab(alert.type);

  const handleClick = () => {
    onDismiss(alert.id);
    navigateTo(tab);
  };

  return (
    <div
      className={`
        relative flex items-start gap-2 p-2 rounded-lg border cursor-pointer
        transition-all duration-300 group
        ${blinking
          ? 'border-orange-500 bg-orange-500/20 animate-pulse'
          : 'border-gray-600 bg-gray-700/40 hover:bg-gray-700/70'}
      `}
      onClick={handleClick}
      title={`Go to ${destLabel(alert.type)}`}
    >
      {/* Dismiss button — stops blink without navigating */}
      <button
        className="absolute top-1 right-1 text-gray-500 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => { e.stopPropagation(); onDismiss(alert.id); }}
        title="Dismiss"
      >
        <X size={11} />
      </button>

      <AlertTypeIcon type={alert.type} />

      <div className="flex-1 min-w-0 pr-3">
        {/* Type badge */}
        <p className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${
          alert.type === 'fuel_request'        ? 'text-orange-400' :
          alert.type === 'maintenance_request' ? 'text-red-400'    :
          'text-blue-400'
        }`}>
          {typeLabel(alert.type)}
        </p>

        {/* Title */}
        <p className="text-white text-xs font-medium leading-tight truncate">
          {alert.title}
        </p>

        {/* Message */}
        {alert.message && (
          <p className="text-gray-400 text-[10px] leading-tight mt-0.5 truncate">
            {alert.message}
          </p>
        )}

        {/* Time + link */}
        <div className="flex items-center justify-between mt-1">
          <span className="text-gray-500 text-[10px]">{alert.timestampRelative}</span>
          <span className="flex items-center gap-0.5 text-[10px] text-blue-400 group-hover:text-blue-300">
            {destLabel(alert.type)} <ExternalLink size={9} />
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Main widget ──────────────────────────────────────────────────────────────
const LiveAlertsWidget = () => {
  const [alerts, setAlerts]         = useState([]);   // current watched alerts
  const [blinking, setBlinking]     = useState({});   // { [id]: true } while blinking
  const [dismissed, setDismissed]   = useState({});   // { [id]: true } dismissed by staff
  const prevIdsRef                  = useRef(new Set());
  const isFirstLoad                 = useRef(true);

  // ── Fetch & diff ─────────────────────────────────────────────────────────
  const loadAlerts = useCallback(async () => {
    try {
      const all = await notificationManager.getAllNotifications();

      // Keep only the three watched types that are still pending
      const watched = all.filter(
        (n) =>
          WATCHED_TYPES.has(n.type) &&
          (!n.status || n.status === 'pending')
      );

      // Detect brand-new IDs (not seen in previous poll)
      const prevIds  = prevIdsRef.current;
      const newIds   = watched
        .map((n) => n.id)
        .filter((id) => !prevIds.has(id));

      // Update the ref with the full current set
      prevIdsRef.current = new Set(watched.map((n) => n.id));

      // On the very first load, don't ring or blink — just populate silently
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
        setAlerts(watched);
        return;
      }

      // New alerts detected → bell + blink
      if (newIds.length > 0) {
        playBellSound();

        setBlinking((prev) => {
          const next = { ...prev };
          newIds.forEach((id) => { next[id] = true; });
          return next;
        });
      }

      setAlerts(watched);
    } catch (err) {
      console.error('[LiveAlertsWidget] load error:', err);
    }
  }, []);

  // ── Poll every 20 s + subscribe to manager push ───────────────────────────
  useEffect(() => {
    loadAlerts();
    const unsub    = notificationManager.subscribe(loadAlerts);
    const interval = setInterval(loadAlerts, 20000);
    return () => {
      unsub();
      clearInterval(interval);
    };
  }, [loadAlerts]);

  // ── Dismiss: stop blink, add to dismissed set ─────────────────────────────
  const handleDismiss = useCallback((id) => {
    setBlinking((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setDismissed((prev) => ({ ...prev, [id]: true }));
  }, []);

  // Visible alerts = not dismissed
  const visible = alerts.filter((a) => !dismissed[a.id]);
  const blinkingCount = Object.keys(blinking).filter(
    (id) => !dismissed[id]
  ).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="bg-gray-800/50 rounded-xl p-3 sm:p-5 border border-gray-700 h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-xl ${blinkingCount > 0 ? 'bg-orange-500/30 animate-pulse' : 'bg-orange-500/20'}`}>
            <Bell className={`w-4 h-4 sm:w-5 sm:h-5 ${blinkingCount > 0 ? 'text-orange-300' : 'text-orange-400'}`} />
          </div>
          <div>
            <p className="text-gray-400 text-xs sm:text-sm">Live Alerts</p>
            {blinkingCount > 0 && (
              <p className="text-orange-400 text-[10px] font-semibold animate-pulse">
                {blinkingCount} new
              </p>
            )}
          </div>
        </div>

        {/* Counter badge */}
        {visible.length > 0 && (
          <span className={`
            text-xs font-bold px-2 py-0.5 rounded-full
            ${blinkingCount > 0
              ? 'bg-orange-500 text-white animate-pulse'
              : 'bg-gray-600 text-gray-300'}
          `}>
            {visible.length}
          </span>
        )}
      </div>

      {/* Alert list */}
      <div className="space-y-2 max-h-[130px] overflow-y-auto pr-0.5">
        {visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-4 text-center">
            <Bell size={20} className="text-gray-600 mb-1" />
            <p className="text-gray-500 text-xs">No pending requests</p>
          </div>
        ) : (
          visible.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              blinking={!!blinking[alert.id]}
              onDismiss={handleDismiss}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default LiveAlertsWidget;